import { Formik } from "formik";
import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { supabase } from "lib/supabaseClient";
import { APP_ROUTES } from "const";
import validationSchema from "./CreateNewPasswordForm.schema";
import {
  useInitialState,
  useSubmitHandler,
} from "./CreateNewPasswordForm.hooks";
import CreateNewPasswordForm from "./CreateNewPasswordForm.component";

function CreateNewPassword() {
  const initialState = useInitialState();
  const navigate = useNavigate();
  const submitHandler = useSubmitHandler();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Supabase sends recovery tokens via the URL hash fragment.
    // The Supabase client automatically picks up the hash and exchanges it
    // for a session. We listen for the PASSWORD_RECOVERY event to know the
    // user arrived here via a valid reset link.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === "PASSWORD_RECOVERY") {
          setReady(true);
        }
      }
    );

    // Also check if there's an error in the hash (e.g. expired link)
    const hash = window.location.hash;
    if (hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace("#", "?"));
      const errorDesc = params.get("error_description");
      setError(errorDesc || "The reset link is invalid or has expired.");
    }

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Password Reset Failed
          </h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            className="text-blue-600 hover:text-blue-800 font-medium"
            onClick={() => navigate(APP_ROUTES.FORGET_PASSWORD)}
          >
            Request a new reset link
          </button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Verifying reset link...</p>
      </div>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialState}
      validationSchema={validationSchema()}
      onSubmit={submitHandler}
    >
      {() => <CreateNewPasswordForm />}
    </Formik>
  );
}

export default memo(CreateNewPassword);
